import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { Viewable } from "by/gamefactory/scene/viewable/Viewable";
import { DefaultMessagesView } from "./view/DefaultMessagesView";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { CancellationToken } from "async/CancellationToken";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { OperationResult } from "./OperationResult";
import { DefaultMessagesResourcePackage } from "./resources/DefaultMessagesResourcePackage";
import { PaymentResult } from "../../../../web3/PaymentResult";

export type DefaultMessageOptions = {
    operation: Promise<OperationResult | PaymentResult>;
    operationCancellationToken?: CancellationToken;
    withProcessingPage: boolean;
}

@injectable()
export class DefaultMessagesScene extends Viewable<OperationResult | PaymentResult> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly canvasScaler: ICanvasScaler;
    private readonly options: DefaultMessageOptions;
    private readonly asyncOperationNotification: AsyncOperationNotification;

    public constructor(
        commonResources: CommonResourcesPackage,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("DefaultMessageOptions") options: DefaultMessageOptions,
        asyncOperationNotification: AsyncOperationNotification
    ) {
        super();
        this.commonResources = commonResources;
        this.canvasScaler = canvasScaler;
        this.options = options;
        this.asyncOperationNotification = asyncOperationNotification;
    }

    @initializer()
    public async initialize(): Promise<void> {
        const defaultMessagesResources = new DefaultMessagesResourcePackage();
        try {
            await this.asyncOperationNotification.autoShowHide(defaultMessagesResources.load());
            const pcs = new PromiseCompletionSource<OperationResult | PaymentResult>();
            this.view = new DefaultMessagesView(
                pcs,
                this.options.operationCancellationToken,
                this.commonResources,
                defaultMessagesResources,
                this.options.operation,
                this.options.withProcessingPage,
                this.canvasScaler
            );
            pcs.result.then((result: OperationResult | PaymentResult) => {
                defaultMessagesResources.destroy();
                this.operationControls.complete(result);
            }, (reason: unknown) => {
                defaultMessagesResources.destroy();
                this.operationControls.error(reason);
            });
        } catch (error: unknown) {
            if (defaultMessagesResources?.loaded) {
                defaultMessagesResources.destroy();
            }
            this.operationControls.error(error);
        }
    }
}
